import { useEffect, useState, type JSX } from 'react';
import { api, onUnauthorized } from './lib/api.ts';
import { live } from './lib/live.ts';
import { isFileDrag } from './lib/images.ts';
import { unlockAudio, useRoute, useViewportHeight } from './lib/hooks.ts';
import { SessionList } from './components/SessionList.tsx';
import { Chat } from './components/Chat.tsx';

type Auth = 'checking' | 'in' | 'out';

export function App(): JSX.Element {
  const [auth, setAuth] = useState<Auth>('checking');
  const { path, go } = useRoute();
  useViewportHeight();

  useEffect(() => {
    const block = (e: DragEvent): void => {
      if (isFileDrag(e.dataTransfer)) e.preventDefault();
    };
    const unlock = (): void => unlockAudio();
    document.addEventListener('dragover', block);
    document.addEventListener('drop', block);
    document.addEventListener('pointerdown', unlock);
    return () => {
      document.removeEventListener('dragover', block);
      document.removeEventListener('drop', block);
      document.removeEventListener('pointerdown', unlock);
    };
  }, []);

  useEffect(() => {
    const drop = (): void => setAuth('out');
    onUnauthorized.add(drop);
    void api
      .me()
      .then(() => setAuth('in'))
      .catch(() => setAuth('out'));
    return () => {
      onUnauthorized.delete(drop);
    };
  }, []);

  useEffect(() => {
    if (auth === 'in') live.start();
    else live.stop();
  }, [auth]);

  let content: JSX.Element;
  if (auth === 'checking') content = <div className="screen splash" />;
  else if (auth === 'out') content = <Login onSuccess={() => setAuth('in')} />;
  else {
    const chat = /^\/s\/([\w-]+)(?:\/(git|settings|deploy|config|task|projects))?$/.exec(path);
    if (chat) {
      const sessionId = chat[1]!;
      const raw = chat[2];
      const tab =
        raw === 'git' || raw === 'settings' || raw === 'task' || raw === 'config'
          ? raw
          : raw === 'deploy'
            ? 'config'
            : 'chat';
      content = (
        <Chat
          sessionId={sessionId}
          tab={tab}
          onBack={() => go('/')}
          onOpen={(id) => go(`/s/${id}`)}
          onTab={(next) => {
            if (next === 'home') go('/');
            else go(next === 'chat' ? `/s/${sessionId}` : `/s/${sessionId}/${next}`);
          }}
        />
      );
    } else {
      const tab =
        path === '/git'
          ? 'git'
          : path === '/deploy' || path === '/config'
            ? 'config'
            : 'home';
      content = (
        <SessionList
          tab={tab}
          onOpen={(id, tab) => go(tab === 'task' ? `/s/${id}/task` : `/s/${id}`)}
          onTab={(next) => go(next === 'home' ? '/' : `/${next}`)}
        />
      );
    }
  }

  return content;
}

function Login({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(password);
      setPassword('');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen login">
      <form className="login-card" onSubmit={(e) => void submit(e)}>
        <div className="login-mark">&gt;_</div>
        <h1>Home agents</h1>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Passphrase"
          autoComplete="current-password"
          // Chrome zooms any input under 16px; 16px keeps the layout still.
          enterKeyHint="go"
        />
        {error && <div className="login-error">{error}</div>}
        <button type="submit" disabled={busy || !password}>
          {busy ? 'Checking...' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}
